import { createAsyncThunk } from '@reduxjs/toolkit';
import { ResponseErrorWithHandled, ResponseSuccess } from '../../shared/types';

import {
  getAccountSettings,
  getPlans,
  getSubscription,
  getSubscriptionPreview,
  reactivateSubscription,
  resumeSubscription,
  revokePausedSubscription,
  subscriptionCustomerPortalSession,
  subscriptionUpdatePaymentMethod,
  subscriptionUpdateStatus,
  subscriptionUpgrade,
  validateCouponCode,
} from './utils/billing-and-subscriptions.api';

import {
  ReactivateResumeSubscriptionRequestPayload,
  SubscriptionCustomerPortalSessionRequestPayload,
  SubscriptionPreviewRequestPayload,
  SubscriptionUpgradeRequestPayload,
  ValidateCouponCodeRequestPayload,
} from './utils/types';

export const getSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('billingAndSubscriptions/getSubscription', async (_args, thunkAPI) => {
  try {
    return await getSubscription();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getPlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('billingAndSubscriptions/getPlans', async (_args, thunkAPI) => {
  try {
    return await getPlans();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSubscriptionPreviewRequest = createAsyncThunk<
  ResponseSuccess,
  SubscriptionPreviewRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('billingAndSubscriptions/getSubscriptionPreview', async (args, thunkAPI) => {
  try {
    return await getSubscriptionPreview(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const subscriptionUpgradeRequest = createAsyncThunk<
  ResponseSuccess,
  SubscriptionUpgradeRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('billingAndSubscriptions/subscriptionUpgrade', async (args, thunkAPI) => {
  try {
    return await subscriptionUpgrade(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const subscriptionUpdateStatusRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/subscriptionUpdateStatus',
  async (args, thunkAPI) => {
    try {
      return await subscriptionUpdateStatus(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const subscriptionCustomerPortalSessionRequest = createAsyncThunk<
  ResponseSuccess,
  SubscriptionCustomerPortalSessionRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/subscriptionCustomerPortalSession',
  async (args, thunkAPI) => {
    try {
      return await subscriptionCustomerPortalSession(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const subscriptionUpdatePaymentMethodRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/subscriptionUpdatePaymentMethod',
  async (_args, thunkAPI) => {
    try {
      return await subscriptionUpdatePaymentMethod();
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const reactivateSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/reactivateSubscriptionRequest',
  async (args, thunkAPI) => {
    try {
      return await reactivateSubscription(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const resumeSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/resumeSubscriptionRequest',
  async (_args, thunkAPI) => {
    try {
      return await resumeSubscription();
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const validateCouponCodeRequest = createAsyncThunk<
  ResponseSuccess,
  ValidateCouponCodeRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/validateCouponCodeRequest',
  async (args, thunkAPI) => {
    try {
      return await validateCouponCode(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

// Revoke Pause Scheduled Subscription
export const revokePauseScheduledSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>(
  'billingAndSubscriptions/revokePauseScheduledSubscriptionRequest',
  async (_args, thunkAPI) => {
    try {
      return await revokePausedSubscription();
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getAccountSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('billingAndSubscriptions/getAccountSettings', async (_args, thunkAPI) => {
  try {
    return await getAccountSettings();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});
